import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { faFileExport } from '@fortawesome/free-solid-svg-icons/faFileExport';
import {
  Title,
  Table,
  Spinner,
  Pagination,
  usePagination,
  Link,
  Button,
  Icon,
  Text,
} from '@appcues/sonar';
import NoData from 'next/assets/images/no-data.svg';
import { Shape as SurveyShape } from 'next/entities/surveys';
import { formatDate } from 'utils';
import { buildURI } from 'utils/CSV';
import {
  Cell,
  DownloadLink,
  HeaderCell,
  HeaderWrapper,
  LoadingWrapper,
  NoResultsMessage,
  TableWrapper,
  Wrapper,
} from './styled';

const transformRows = answers =>
  answers.map(({ fieldAnswers, userId, timestamp }) => ({
    userId,
    timestamp,
    ...fieldAnswers.reduce(
      (acc, { fieldId, value }) => ({
        ...acc,
        [fieldId.replaceAll("'", '')]: value,
      }),
      {}
    ),
  }));

const renderColumnElement = (values, columnId) => {
  switch (columnId) {
    case 'userId':
      return (
        <Link href={`/audience/users/${values[columnId]}/properties`}>
          {values[columnId]}
        </Link>
      );
    case 'timestamp':
      return formatDate(
        new Date(values[columnId]).getTime(),
        'MMM D, YYYY h:mm A'
      );
    default:
      return values[columnId];
  }
};

export const SurveyResponses = ({
  surveys = {},
  onLoad,
  flowId,
  hasSurveysExport,
}) => {
  const [loading, setLoading] = React.useState(true);

  const [sortConfig, setSortConfig] = useState({
    column: 'timestamp',
    direction: 'ASC',
  });

  const handleSort = column => {
    setSortConfig(prevState => {
      if (prevState?.column === column) {
        return {
          column,
          direction: prevState.direction === 'ASC' ? 'DESC' : 'ASC',
        };
      }
      return {
        column,
        direction: 'ASC',
      };
    });
  };

  const sort = useCallback(
    (a, b) => {
      const columnA = `${a[sortConfig?.column]}`;
      const columnB = `${b[sortConfig?.column]}`;

      if (sortConfig?.column === 'timestamp') {
        if (sortConfig?.direction === 'DESC') {
          return new Date(columnA).getTime() - new Date(columnB).getTime();
        }
        return new Date(columnB).getTime() - new Date(columnA).getTime();
      }

      if (sortConfig?.direction === 'ASC') {
        return columnA.localeCompare(columnB, undefined, {
          numeric: true,
        });
      }
      return columnB.localeCompare(columnA, undefined, {
        numeric: true,
      });
    },
    [sortConfig?.column, sortConfig?.direction]
  );

  useEffect(() => {
    const load = async () => {
      await onLoad?.();
      setLoading(false);
    };

    load();
  }, []);
  const { pageIndex, pageSize, setPagination } = usePagination();

  const columns = useMemo(
    () => [
      { key: 'userId', label: 'User ID' },
      ...(surveys.labels || []).map(({ fieldId: key, label }) => ({
        key,
        label,
      })),
      { key: 'timestamp', label: 'Date received' },
    ],
    [surveys?.labels]
  );

  const transformedRows = useMemo(
    () => transformRows(surveys.answers || []),
    [surveys?.answers]
  );

  const sortedRows = useMemo(
    () => [...transformedRows].sort(sort),
    [transformedRows, sort]
  );

  const rows = useMemo(
    () =>
      sortedRows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [sortedRows, pageIndex, pageSize]
  );

  return (
    <Wrapper>
      <HeaderWrapper>
        <Title>Survey Responses</Title>
        {hasSurveysExport && (
          <DownloadLink
            download={flowId}
            target="_blank"
            href={buildURI(sortedRows, undefined, columns)}
          >
            {surveys.answers && surveys.answers.length > 0 && (
              <Button variant="secondary">
                <Icon icon={faFileExport} />
                Export
              </Button>
            )}
          </DownloadLink>
        )}
      </HeaderWrapper>
      {surveys.answers && !loading ? (
        <>
          <TableWrapper>
            {surveys.answers.length === 0 ? (
              <NoResultsMessage>
                <img src={NoData} alt="No data found" />
                {surveys.totalAnswersCount > 0
                  ? 'No survey responses for the selected dates. Adjust date range selection to view responses.'
                  : 'No survey responses yet.'}
              </NoResultsMessage>
            ) : (
              <Table.Root>
                <Table.Thead>
                  <Table.Tr>
                    {columns.map(({ key, label }) => (
                      <HeaderCell
                        sortable
                        sortDirection={
                          sortConfig.column === key
                            ? sortConfig?.direction
                            : undefined
                        }
                        onClick={() => handleSort(key)}
                        key={key}
                      >
                        {label}
                      </HeaderCell>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map(values => (
                    <Table.Tr key={values.userId + values.timestamp}>
                      {columns.map(column => (
                        <Cell key={column.key + values.userId}>
                          {renderColumnElement(values, column.key)}
                        </Cell>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table.Root>
            )}
          </TableWrapper>
          {surveys?.answers.length > 10 && (
            <Pagination
              totalResults={(surveys?.answers || []).length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPagination={setPagination}
            />
          )}
        </>
      ) : (
        <LoadingWrapper>
          <Spinner />
          <Text>Loading your responses...this might take a minute.</Text>
        </LoadingWrapper>
      )}
    </Wrapper>
  );
};

SurveyResponses.propTypes = {
  surveys: SurveyShape,
  onLoad: PropTypes.func,
  flowId: PropTypes.string,
  hasSurveysExport: PropTypes.bool,
};

export default SurveyResponses;
